// src/Components/Invoice/CustomHooks/useCreateProformaInvoice.js

import { useState, useEffect, memo } from "react";
import axios from "axios";
import config from "../../../Helpers/config.json";
import { errorMessages } from "../../../Helpers/ErrorMessages";
import { useReusableNewWholeSales } from "./useReusableNewWholeSales";
import { history } from "../../../Helpers/history";   // ← Import this

export const useCreateProformaInvoice = (actions, invoice_number = '', saletype = '') => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    const user_id = user?.user_id || 1;

    const [submitted, setSubmitted] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState('idle');
    const [isBlocking, setIsBlocking] = useState(false);

    const { stateFunctions, stateVariables } = useReusableNewWholeSales(actions, invoice_number, false, saletype);

    const {
        customer,
        rows,
        totals,
        initialState,
        state
    } = stateVariables;

    const { setRows, setCustomer, setTotals } = stateFunctions;

    // Auto calculate totals when rate or quantity changes
    useEffect(() => {
        const grandTotal = rows.reduce((sum, item) => {
            const qty = Number(item.product_quantity) || 0;
            const rate = Number(item.product_rate) || 0;
            return sum + (qty * rate);
        }, 0);

        setTotals({
            grand_total_price: grandTotal,
            n_total: grandTotal,
        });
    }, [rows, setTotals]);

    const handleChangeRate = (e, index) => {
        const value = e.target.value;
        const newRate = value === '' ? 0 : parseFloat(value);

        const updatedRows = rows.map((row, i) => {
            if (i === index) {
                const qty = Number(row.product_quantity) || 0;
                return {
                    ...row,
                    product_rate: newRate,
                    total_price: qty * newRate
                };
            }
            return row;
        });
        setRows(updatedRows);
        setIsBlocking(true);
    };

    const handleChangeQuantity = (e, index) => {
        const value = e.target.value;
        const newQty = value === '' ? 0 : parseFloat(value);

        const updatedRows = rows.map((row, i) => {
            if (i === index) {
                const rate = Number(row.product_rate) || 0;
                return {
                    ...row,
                    product_quantity: newQty,
                    total_price: newQty * rate
                };
            }
            return row;
        });
        setRows(updatedRows);
        setIsBlocking(true);
    };

    const handleSaveProformaInvoice = async (event) => {
        event.preventDefault();

        const { customer_id } = customer;
        const { invoice_date } = state;
        const { grand_total_price } = totals;

        setSubmitted(true);

        if (!customer_id) {
            actions.snackbarActions.infoSnackbar("Please select a customer");
            return;
        }
        if (!invoice_date) {
            actions.snackbarActions.infoSnackbar("Please select proforma date");
            return;
        }
        if (rows.length === 0) {
            actions.snackbarActions.infoSnackbar("Please add at least one product");
            return;
        }

        const invalidIndex = rows.findIndex(item => 
            !item.product_name || 
            !item.product_quantity || Number(item.product_quantity) <= 0 ||
            !item.total_price || Number(item.total_price) <= 0
        );

        if (invalidIndex !== -1) {
            actions.snackbarActions.infoSnackbar(`Please complete Row ${invalidIndex + 1}`);
            return;
        }

        setIsSubmitted('pending');
        setIsBlocking(true);

        const bill_details = rows.map(row => ({
            product_id: row.product_id,
            quantity: Number(row.product_quantity),
            rate: Number(row.product_rate || 0),
            total_price: Number(row.total_price),
            discount: Number(row.discount || 0),
        }));

        try {
            const response = await axios.post(
                `${config.epharmUrl}/Cinvoice/insert_proforma_invoice`,
                {
                    customer_id: Number(customer_id),
                    total_amount: Number(grand_total_price),
                    memo: state.inva_details || "",
                    bill_details,
                    invoice_date,
                    is_wholesale: saletype === "wholesale",
                },
                // { headers: { user_id } }
            );

            if (response.data.status === 200 || response.data.success) {
                actions.snackbarActions.successSnackbar("Proforma Invoice created successfully!");

                // Reset form
                setRows(initialState);
                setCustomer({ customer_name: "", customer_id: "" });
                setTotals({ grand_total_price: 0, n_total: 0 });
                setSubmitted(false);
                setIsSubmitted('resolved');
                setIsBlocking(false);
                history.push("/proforma-invoices");
            }
        } catch (error) {
            errorMessages(error, null, actions);
            setIsSubmitted('rejected');
            setIsBlocking(false);
        }
    };

    const handleCloseSnackbar = () => {
        actions.snackbarActions.hideSnackBar();   // This is how other pages do it
    };

    const saveDispensarProformaInvoice = handleSaveProformaInvoice;

    const isPending = isSubmitted === 'pending';

    return {
        submitted,
        stateVariables: { 
            ...stateVariables, 
            isBlocking,
            handleChangeRate,
            handleChangeQuantity,
        },
        stateFunctions: { ...stateFunctions, setIsBlocking },
        isPending,
        handleSaveProformaInvoice,
        saveDispensarProformaInvoice,
        handleCloseSnackbar,          // ← Added this
    };
};